import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import { retrieveMatterFileMetadata } from "@/lib/data/requests/matters/matter-file";
import {
  createOrdinaryFile,
  fileDestination,
  fileOperationMessage,
} from "@/lib/data/requests/files/canonical-files";
import { adaptFile } from "@/lib/data/requests/files/file-adapter";
import {
  createMatterFileReferenceV2,
  createMatterFileV1,
  generateMatterFilePdf,
  duplicateMatterFileV1,
  deleteMatterFileReferenceV2,
  mergeMatterFilesV2,
  retrieveMatterFiles,
  updateMatterFileV1,
} from "@/lib/data/requests/matters/matter-file";
import { deleteFile } from "@/lib/data/requests/files/file";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  type APIError,
  type APIResponse,
  type Matter,
  type MatterFile,
} from "@/lib/types";
import {
  ensureFileNameHasExtension,
  getFileNameParts,
} from "@/lib/utils/matterFile";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

type MatterFileParams =
  | {
      clientMethod: "put_object";
      fileName: string;
    }
  | {
      clientMethod: "get_object" | "delete_object";
      key: string;
    };

type MatterFileParentData = {
  matterId?: string;
  massTortId?: string;
};

const getMatterFileParentId = (data: MatterFileParentData | string) =>
  typeof data === "string" ? data : data.massTortId || data.matterId;

const getMatterFileParentQueryKey = (data: MatterFileParentData | string) =>
  typeof data === "string" || !data.massTortId
    ? [queryKeys.matters, getMatterFileParentId(data)]
    : [queryKeys.massTorts, data.massTortId];

const getMatterFileParentRootQueryKey = (
  data: MatterFileParentData | string,
) =>
  typeof data === "string" || !data.massTortId
    ? [queryKeys.matters]
    : [queryKeys.massTorts];

const getMatterFileId = (file: MatterFile) => file.id;

const upsertMatterFile = (
  files: MatterFile[] | undefined,
  fileToUpsert: MatterFile,
) => {
  if (!files) return [fileToUpsert];

  const fileToUpsertId = getMatterFileId(fileToUpsert);
  if (!fileToUpsertId) return files;

  const existingIndex = files.findIndex(
    (file) => getMatterFileId(file) === fileToUpsertId,
  );

  if (existingIndex === -1) {
    return [...files, fileToUpsert];
  }

  return files.map((file, index) =>
    index === existingIndex ? fileToUpsert : file,
  );
};

export const retrieveMatterFilesQueryOptions = (
  matterId: Parameters<typeof retrieveMatterFiles>[0],
) => {
  return {
    queryKey: [...getMatterFileParentQueryKey(matterId), "files"],
    enabled: !!getMatterFileParentId(matterId),
    queryFn: async () => retrieveMatterFiles(matterId),
    fetchOnWindowFocus: false,
  };
};

export const useCreateMatterFile = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Parameters<typeof createMatterFileV1>[0]) => {
      if (!data.isFolder && !data.file)
        return { ...(await createMatterFileV1(data)), publishedVersion: null };
      if (
        data.file &&
        ["heic", "heif"].includes(
          getFileNameParts(data.file.name).fileExtension.toLowerCase(),
        )
      ) {
        const name = ensureFileNameHasExtension(
          data.name,
          getFileNameParts(data.file.name).fileExtension,
        );
        const signed = await generateS3ItemPreSignedUrlV1("matter-file")({
          clientMethod: "put_object",
          fileName: name,
        });
        const uploaded = await fetch(signed.data.presignedUrl, {
          method: "PUT",
          headers: { "Content-Type": data.file.type },
          body: data.file,
        });
        if (!uploaded.ok)
          throw new Error("The file upload failed. Please try again.");
        return {
          ...(await createMatterFileV1({
            ...data,
            name,
            presignedUrlKey: signed.data.key,
          })),
          publishedVersion: null,
        };
      }
      const destination = fileDestination(
        data.massTortId ? "mass_tort" : "matter",
        data.idLocation,
        data.massTortId || data.matterId,
      );
      const result = await createOrdinaryFile({
        name: data.name,
        description: data.description,
        isFolder: data.isFolder,
        file: data.file,
        destination,
      });
      const metadata = result.isFolder
        ? undefined
        : (
            await retrieveMatterFileMetadata(
              data.massTortId
                ? { massTortId: data.massTortId }
                : { matterId: data.matterId ?? "" },
            )
          ).data.find((item) => item.id === result.id);
      let file = adaptFile<MatterFile>(result, { ...data, ...metadata });
      if (data.tags?.length) {
        file = (
          await updateMatterFileV1({
            ...(data.massTortId
              ? { massTortId: data.massTortId }
              : { matterId: data.matterId ?? "" }),
            id: result.id,
            dateModified: result.dateModified,
            tags: data.tags,
          })
        ).data;
      }
      return {
        data: file,
        publishedVersion: result.publishedVersion,
        meta: { userMsg: "File saved" },
      };
    },

    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toast.error(fileOperationMessage(error, "Error creating matter file"));
      }
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: getMatterFileParentQueryKey(variables),
      });
      await queryClient.invalidateQueries({
        queryKey: getMatterFileParentRootQueryKey(variables),
      });
    },
    onSuccess: (response, variables) => {
      if (variables.massTortId) {
        if (showToast) {
          toast.success(
            variables.isFolder
              ? "Folder created successfully"
              : variables.file
                ? response.publishedVersion
                  ? "File Version Uploaded"
                  : "File uploaded successfully"
                : "File created successfully",
          );
        }
        return;
      }

      queryClient.setQueryData<APIResponse<Matter>>(
        getMatterFileParentQueryKey(variables),
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              files: upsertMatterFile(oldData.data.files, response.data),
            },
          };
        },
      );

      if (showToast) {
        toast.success(
          variables.isFolder
            ? "Folder created successfully"
            : variables.file
              ? response.publishedVersion
                ? "File Version Uploaded"
                : "File uploaded successfully"
              : "File created successfully",
        );
      }
    },
  });
};

export const useUpdateMatterFile = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterFileV1>[0]) =>
      updateMatterFileV1(data),
    onMutate: (data) => {
      if (data.massTortId) {
        return;
      }
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              files: oldData.data.files.map((file) => {
                const fileId = file.id;
                if (fileId !== data.id) {
                  return file;
                }

                return {
                  ...file,
                  ...data,
                  idLocation: data.idLocation ?? file.idLocation,
                };
              }),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData && variables.matterId) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      if (showToast) {
        toast.error(fileOperationMessage(error, "Error updating matter file"));
      }
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: getMatterFileParentQueryKey(variables),
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("File updated successfully");
      }
    },
  });
};

export const useDeleteMatterFile = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Pick<MatterFile, "id"> & MatterFileParentData) =>
      deleteFile(data.id),
    onMutate: (data) => {
      if (data.massTortId) {
        return;
      }
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              files: oldData.data.files.filter((file) => file.id !== data.id),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData && variables.matterId) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      if (showToast) {
        toast.error("Error deleting matter file");
      }
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: getMatterFileParentQueryKey(variables),
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("File deleted successfully");
      }
    },
  });
};

export const useGenerateMatterFilePreSignedUrl = () => {
  return useMutation({
    mutationFn: (data: MatterFileParams) =>
      generateS3ItemPreSignedUrlV1("matter-file")(data),
    onError: () => {
      toast.error("Error generating pre-signed URL");
    },
  });
};

export const useCreateMatterFileReference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterFileReferenceV2>[0]) =>
      createMatterFileReferenceV2(data),
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: getMatterFileParentQueryKey(variables),
      });
    },
    onSuccess: () => {
      toast.success("File Reference created successfully");
    },
    onError: () => {
      toast.error("Error creating file reference");
    },
  });
};

export const useDeleteMatterFileReference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Parameters<typeof deleteMatterFileReferenceV2>[0],
    ) => {
      return deleteMatterFileReferenceV2(data);
    },
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              fileReferences: oldData.data.fileReferences.filter(
                (reference) => reference.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error deleting file reference");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("File Reference deleted successfully");
    },
  });
};

export const useMergeMatterFiles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof mergeMatterFilesV2>[0]) =>
      mergeMatterFilesV2(data),
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: getMatterFileParentQueryKey(variables),
      });
    },
    onSuccess: () => {
      toast.success("Files merged successfully");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error merging files");
    },
  });
};

export const useDuplicateMatterFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof duplicateMatterFileV1>[0]) =>
      duplicateMatterFileV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error duplicating file");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: getMatterFileParentRootQueryKey(variables),
      });
    },
    onSuccess: () => {
      toast.success("File duplicated successfully");
    },
  });
};

export const useGenerateMatterFilePdf = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof generateMatterFilePdf>[0]) =>
      generateMatterFilePdf(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error converting file to PDF");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.files] });
      await queryClient.invalidateQueries({
        queryKey: getMatterFileParentRootQueryKey(variables),
      });
    },
    onSuccess: () => {
      toast.success("PDF created successfully");
    },
  });
};
